import { combineReducers } from 'redux'

/**
 * Returns an immutable reducer function, that runs all provided reducers in sequence and propagates state changes
 * through the reducer chain.
 * This can be useful to combine state changes of several (immutable) reducers into one final immutable reducer.
 * @param {Object} initialState - The initial state to be provided to the reducer
 * @param {function[]} reducers - The reducers, whose state changes should be combined
 */
export function composeReducers(initialState, ...reducers) {
    return (state = initialState, action) => {
        for (const reducer of reducers) {
            state = reducer(state, action)
        }
        return state
    }
}

/**
 * Builds a combined reducer from static and dynamically loaded reducers
 *
 * @param {object} initialReducers  - Static reducers known at bootstrapping time
 * @param {object} loadableReducers - Collected reducers from dynamically imported modules
 * @return {Function}               - A reducer function, to be used by createStore and replaceReducer from Redux
 */
export function createReducer(initialReducers, loadableReducers) {
    return combineReducers({
        ...initialReducers,
        ...loadableReducers
    })
}

/**
 * Convenience function to bind the createReducer function above to the static reducers of an application.
 * The result is meant to be exported in the target application, e.g.:
 * export const createReducer = createReducerFactory(initialReducers)
 *
 * @param {object} initialReducers  - Static reducers known at bootstrapping time
 * @return {Function}               - The reducer factory above
 */
export function createReducerFactory(initialReducers) {
    return createReducer.bind(null, initialReducers)
}
